/* eslint-disable simple-import-sort/imports */
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/nextjs';
import { authenticationCompleted, clearTokens, setToken, setRefreshToken } from '@/utils/helpers/auth';
import { pageRoutes, apiRoutes } from '@/utils/routes';
import { useMultiFactorAuthentication } from '@/utils/hooks/useMultiFactorAuthentication';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { IconArrowNarrowLeft } from '@tabler/icons-react';
import { getRedirectUrlWithRegion } from '@/utils/helpers/redirect';
import handleBadResponse from '@/utils/fetch/handleBadResponse';
import { yupResolver } from '@hookform/resolvers/yup';
import { getRegion } from '@/utils/i18n/regions';

import Link from 'next/link';
import { Button, TextInput, PasswordInput, Checkbox } from '@/_components';
import { useEffect, useState } from 'react';
import { loginSchemaConstructor } from './schema';

interface IFormValues {
  username: string;
  password: string;
  rememberMe: boolean;
}

const login = async (data: IFormValues) => {
  const apiUrl = getRegion()?.apiUrl || '';

  return await fetch(`${apiUrl}${apiRoutes.getTokenByPassword}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(data),
  });
};

const LoginForm = () => {
  const { t } = useTranslation();
  const [rememberMe, setRememberMe] = useState(true);

  useEffect(() => {
    const adminRememberMe = localStorage.getItem('YO_ADMIN_REMEMBER_ME');

    setRememberMe(adminRememberMe === 'true');
  }, []);

  const schema = loginSchemaConstructor(t);

  const loginMutation = useMutation<Response, Error, IFormValues>({
    mutationKey: ['login'],
    mutationFn: login
  });

  const router = useRouter();
  const mfa = useMultiFactorAuthentication();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<IFormValues>({
    resolver: yupResolver(schema),
    reValidateMode: 'onSubmit',
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const submit = async (values: IFormValues) => {
    clearTokens(); // This can be removed when we redirect with valid session
    setRememberMe(values.rememberMe);
    localStorage.setItem('YO_ADMIN_REMEMBER_ME', String(values.rememberMe));

    try {
      if (router.query.targetPath)
        sessionStorage.setItem('redirectAfterAuthentication', decodeURIComponent(router.query.targetPath as string));

      const res = await loginMutation.mutateAsync(values);

      if (!res.ok) return handleBadResponse(res, schema, setError);

      const loginResponseData = await res.json();

      setToken(loginResponseData.token);

      if (loginResponseData.refresh_token) setRefreshToken(loginResponseData.refresh_token); // When MFA is enabled no refresh token is returned.

      if (mfa.enabled) {
        if (!loginResponseData['2FAEnabled']) router.push(getRedirectUrlWithRegion(pageRoutes.mFARegister));
        else if (loginResponseData['2FAEnabled']) router.push(getRedirectUrlWithRegion(pageRoutes.mFACode));
        else toast.error(t('globals:error'));
      } else authenticationCompleted(router);
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  return (
    <>
      <div data-ui-test-class="login" className="w-full">
        <form onSubmit={handleSubmit(submit)} method="post">
          <h1 data-testid="login-title" className="mb-4 text-center text-2xl font-bold">
            {t('login:title')}
          </h1>

          <div className="flex flex-col gap-2">
            <TextInput
              {...register('username')}
              id="username"
              label={t('login:username')}
              inputPlaceHolder={t('login:username')}
              errorMessage={errors.username?.message}
              enablePasswordManagerAutofill
            />

            <PasswordInput
              {...register('password')}
              id="password"
              label={t('globals:password:default')}
              placeholder={t('login:password')}
              error={errors.password?.message}
              enablePasswordManagerAutofill
            />
          </div>

          <div className="mb-8 mt-4">
            <Checkbox {...register('rememberMe')} defaultChecked={rememberMe} label={t('login:rememberMe')} />
          </div>
          <div className="mb-3 flex flex-col items-center justify-center">
            <div className="w-full">
              <Button type="submit" block className="w-full py-4" isLoading={isSubmitting} disabled={isSubmitting}>
                {t('login:button')}
              </Button>
            </div>
          </div>
        </form>
      </div>

      <div className=" mb-5 text-center">
        <Link href={getRedirectUrlWithRegion(pageRoutes.forgotPassword)} className="text-accent-blue hover:text-black">
          {t('login:forgotPassword')}
        </Link>
      </div>

      <div className="mt-5">
        <Link href="/" className="text-accent-blue hover:text-black">
          <div className="inline-flex items-center justify-start">
            <IconArrowNarrowLeft size={36} stroke={1} />
            {t('pages:login:changeRegion')}
          </div>
        </Link>
      </div>
    </>
  );
};

export default LoginForm;
